import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Card,
} from "react-bootstrap";
import axios from "axios";

const Appointment = () => {
  const { id } = useParams();
  const [userId, setUserId] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [remark, setRemark] = useState("");
  const [address, setAddress] = useState("");
  const [alternateNumber, setAlternateNumber] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // Fetch doctor details
  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/doctor/${id}`
        );
        setDoctor(res.data.doctor);
      } catch (err) {
        console.error(err);
        navigate("/not-found");
      }
    };

    const user = localStorage.getItem("user");
    if (user) {
      const userData = JSON.parse(user);
      setUserId(userData.id);
      setAddress(userData.address || "");
    } else {
      navigate("/login");
    }

    fetchDoctor();
    window.scrollTo(0, 0);
  }, [id, navigate]);

  // Calculate consultation charge based on time
  const getCharges = (time) => {
    const hour = parseInt(time.split(":")[0]);
    return hour >= 9 && hour < 19 ? 500 : 1000;
  };

  // Handle form submission and send appointment data to backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId || !id || !appointmentDate || !appointmentTime) {
      setErrorMessage("All fields are required!");
      return;
    }

    if (alternateNumber && !/^\d{10}$/.test(alternateNumber)) {
      setErrorMessage("Alternate number must be exactly 10 digits.");
      return;
    }

    const fullAddress = alternateNumber
      ? `${address}, Alternate number: ${alternateNumber}`
      : address;

    const consultation_charge = getCharges(appointmentTime); 

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/book-appointment`,
        {
          user_id: userId,
          doctor_id: id,
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          address: fullAddress,
          charge: consultation_charge, 
          remark: remark,
          remark2: 'NA',
        }
      );
      setSuccessMessage(response.data.message);
      setErrorMessage("");

      setTimeout(() => {
        navigate("/my-booking");
      }, 3000);
    } catch (error) {
      const serverMessage = error.response?.data?.message || error.message;
      setErrorMessage("Warning: " + serverMessage);
    }
  };

  return (
    <Container className="mt-5 py-5">
      <h2 className="text-center text-primary mb-4">Doctor Consultation Booking</h2>
      <Row>
        <Col md={5} className="mb-4">
          <Card className="shadow-sm bg-light border-0 text-center">
            <Card.Body>
              <h5 className="text-primary fw-bold mb-3">Consultation Charges</h5>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <i className="bi bi-sun-fill text-warning me-2"></i>
                  <strong>Daytime (9:00 AM – 7:00 PM):</strong> ₹500
                </li>
                <li>
                  <i className="bi bi-moon-stars-fill text-primary me-2"></i>
                  <strong>Nighttime (7:00 PM – 9:00 AM):</strong> ₹1000
                </li>
              </ul>
              <p className="mt-3 text-dark" style={{ fontSize: "0.9rem" }}>
                Charges may vary based on the time of appointment. 
              </p>
            </Card.Body>
          </Card>

          {doctor && (
            <Card>
              <Card.Img
                variant="top"
                src={`${process.env.REACT_APP_API_URL}/${doctor.profile_pic}`}
                alt={doctor.name}
                style={{
                  height: "200px",
                  objectFit: "contain",
                  borderTopLeftRadius: "15px",
                  borderTopRightRadius: "15px",
                }}
              />
              <Card.Body style={{ textAlign: "center", background: "#fff" }}>
                <h4 className="fw-bold">{doctor.name}</h4>
                <hr />
                <p>{doctor.description}</p>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={7}>
          <Card className="shadow-lg p-4" style={{ backgroundColor: "#f0f8ff" }}>
            <h4 className="text-center text-success mb-3">Book an Appointment</h4>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="appointmentDate" className="mb-2">
                <Form.Label className="fw-bold">Date</Form.Label>
                <Form.Control
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  min={today}
                  required
                />
              </Form.Group>

              <Form.Group controlId="appointmentTime" className="mb-2">
                <Form.Label className="fw-bold">Time</Form.Label>
                <Form.Control
                  type="time"
                  value={appointmentTime}
                  onChange={(e) => setAppointmentTime(e.target.value)}
                  required
                />
                {appointmentTime && (
                  <div className="mt-2 text-info fw-semibold">
                    Estimated Charges: ₹{getCharges(appointmentTime)}
                  </div>
                )}
              </Form.Group>

              <Form.Group controlId="address" className="mb-2">
                <Form.Label className="fw-bold">Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group controlId="alternateNumber" className="mb-2">
                <Form.Label className="fw-bold">Alternate Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Optional"
                  value={alternateNumber}
                  onChange={(e) => setAlternateNumber(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="remark" className="mb-2">
                <Form.Label className="fw-bold">Remark</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Any medical concern or note..."
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </Form.Group>

              <div className="d-grid mt-4">
                <Button variant="primary" className="fw-bold" type="submit">
                  Confirm Appointment
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Appointment;
